// src/components/pcr/sections/Documentation.tsx
import React, { useRef, useEffect } from 'react';
import { useForm } from '../../../contexts/FormContext';
import SectionHeading from '../common/SectionHeading';

interface DocumentationProps {
  sectionHighlight?: string;
}

const Documentation: React.FC<DocumentationProps> = ({ sectionHighlight }) => {
  const { formData, updateFormField } = useForm();
  const { documentation } = formData;

  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionHighlight || !sectionRef.current) return;
    
    sectionRef.current.classList.add('section-highlight');
    sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    setTimeout(() => {
      if (sectionRef?.current) {
        sectionRef.current.classList.remove('section-highlight');
      }
    }, 2000);
  }, [sectionHighlight]);

  const handleChange = (field: string, value: string) => {
    updateFormField('documentation', field, value);
  };

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  return (
    <div ref={sectionRef} className="bg-gradient-to-br from-rose-50 to-white p-4 rounded-lg mb-6 shadow-md">
      <SectionHeading 
        title="Documentation" 
        description="Record all patient care information, interventions, and communications accurately. Inacurate terminology and poor grammar will affect your score."
      />
      
      <div className="space-y-3">
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="documentation-narrative" className="block text-xl font-medium text-gray-700">Narrative</label>
                <span className="ml-2 text-sm text-gray-500">(250-500 words)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(documentation.narrative)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: Write a detailed narrative of the patient encounter, starting from your initial arrival at the scene through to your final assessment. Your narrative should flow logically, highlighting key observations, assessments, and any changes in the patient's condition. Use clear and concise language, focusing on the most relevant information gathered and your clinical decisions throughout the scenario. Connect the patient's presentation with your treatment, using your own judgment to include the critical details that support your decisions. This narrative should reflect your understanding of what a well-rounded paramedic report should look like.
            </p>
            <textarea 
              id="documentation-narrative" 
              className="p-2 text-xs md:text-base mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={documentation.narrative}
              onChange={(e) => handleChange('narrative', e.target.value)}
              aria-labelledby="label-documentation-narrative"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="documentation-radio-report" className="block text-xl font-medium text-gray-700">Radio Report</label>
                <span className="ml-2 text-sm text-gray-500">(250-500 words)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(documentation.radioReport)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: Narrate a radio report just as if you were calling it in. Prioritize the information based on the patient's presentation and your field diagnosis. Use the Region XI Guidelines as a framework for your report, and remember to adjust your format accordingly.
            </p>
            <textarea 
              id="documentation-radio-report" 
              className="p-2 text-xs md:text-base mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={documentation.radioReport}
              onChange={(e) => handleChange('radioReport', e.target.value)}
              aria-labelledby="label-documentation-radio-report"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="documentation-drug-cards" className="block text-xl font-medium text-gray-700">Drug Cards</label>
                <span className="ml-2 text-sm font-bold text-red-600">(Upload to Brightspace when submitting your assignment)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(documentation.drugCards)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: You must create a drug card for any medication the patient takes or any medication listed in this assignment, including protocols you've reference. (Including oxygen)
            </p>
            <textarea 
              id="documentation-drug-cards" 
              className="p-2 text-xs md:text-base mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={documentation.drugCards}
              onChange={(e) => handleChange('drugCards', e.target.value)}
              aria-labelledby="label-documentation-drug-cards"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="documentation-format-+-resources" className="block text-xl font-medium text-gray-700">Format + Resources</label>
              </div>
              <span className="text-xs text-gray-500">{countWords(documentation.formatAndResources)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: In this section, <span className="font-bold text-red-600">document the protocols you referenced including Protocol Title, page number, Region Name</span>. List any other sources used, including author, edition, publication details, and screenshots. Points will also be deducted in this section for any unprofessional or incorrect grammar, so write clearly and carefully.
            </p>
            <textarea 
              id="documentation-format-+-resources" 
              className="p-2 text-xs md:text-base mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={documentation.formatAndResources}
              onChange={(e) => handleChange('formatAndResources', e.target.value)}
              aria-labelledby="label-documentation-format-+-resources"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;